'use client';

/**
 * ObeyaPlanLensGapCard — budget against the cheapest whole plan. One bar:
 * what the picks cost so far, the budget still left, and the overage past the
 * budget mark, with the budget and cheapest-whole-plan marks on it; the key
 * beneath names the figures, and the mid and premium totals close it.
 *
 * A renderer: every segment width, mark position and figure arrives from the
 * BFF. Nothing is derived here.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensGapCard
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { PlanLensFrame } from './plan-lens-frame';
import { usePlanLenses, money } from './use-plan-lenses';

export function ObeyaPlanLensGapCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_gap',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? `${d.labels.gap_title}: ${money(d.gap.data.budget.plan_total_selected)}` : 'Plan gap',
    json: d ? d.gap : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const b = d.gap.data.budget;
  const geo = d.gap.data.paint;
  const over = b.lowest_tier_vs_budget === 'over';

  return (
    <PlanLensFrame title={L.gap_title} meta={L.gap_meta}>
      <div className="relative h-[18px] rounded-md bg-muted/40 mt-1 mb-14">
        <div className="absolute inset-y-0 left-0 rounded-l-md bg-primary" style={{ width: `${geo.bar_selected_pct ?? 0}%` }} />
        <div
          className="absolute inset-y-0 border-y border-border"
          style={{
            left: `${geo.bar_selected_pct ?? 0}%`, width: `${geo.bar_left_pct ?? 0}%`,
            background: 'repeating-linear-gradient(135deg, transparent 0 5px, hsl(var(--border)) 5px 6px)',
          }}
        />
        <div className="absolute inset-y-0 bg-red-400/85" style={{ left: `${geo.bar_budget_pct ?? 0}%`, width: `${geo.bar_over_pct ?? 0}%` }} />
        {geo.bar_budget_pct != null && b.approved_budget != null && (
          <div className="absolute -top-1.5 -bottom-1.5 w-[2px] bg-foreground" style={{ left: `${geo.bar_budget_pct}%` }}>
            <span className={cn(text.muted, 'absolute top-full mt-1.5 right-1 text-right leading-snug whitespace-nowrap')}>
              {L.budget}<br /><b className="text-foreground tabular-nums">{money(b.approved_budget)}</b>
            </span>
          </div>
        )}
        {geo.bar_lowest_tier_pct != null && b.plan_total_at_lowest_tier != null && (
          <div className={cn('absolute -top-1.5 -bottom-1.5 w-[2px]', over ? 'bg-red-400' : 'bg-foreground')} style={{ left: `${geo.bar_lowest_tier_pct}%` }}>
            <span className={cn(text.muted, 'absolute top-full mt-1.5 left-1 leading-snug whitespace-nowrap')}>
              {L.cheapest_whole_plan}<br /><b className="text-foreground tabular-nums">{money(b.plan_total_at_lowest_tier)}</b>
            </span>
          </div>
        )}
      </div>

      <div className={cn(text.muted, 'flex flex-wrap gap-x-7 gap-y-2')}>
        <span><i className="inline-block w-2.5 h-2.5 rounded-sm bg-primary mr-2 align-[-1px]" />{L.selected_so_far} <b className="text-foreground tabular-nums">{money(b.plan_total_selected)}</b></span>
        {b.budget_left_after_selected != null && (
          <span><i className="inline-block w-2.5 h-2.5 rounded-sm border border-border bg-muted/40 mr-2 align-[-1px]" />{L.budget_left} <b className="text-foreground tabular-nums">{money(b.budget_left_after_selected)}</b></span>
        )}
        <span>{L.open_requirements} <b className="text-foreground tabular-nums">{d.gap.data.requirements.open}</b></span>
        {b.lowest_tier_gap != null && (
          <span><i className="inline-block w-2.5 h-2.5 rounded-sm bg-red-400 mr-2 align-[-1px]" />{L.cheapest_vs_budget} <b className="text-foreground tabular-nums">{money(b.lowest_tier_gap)} {L[`${b.lowest_tier_vs_budget}_budget`]}</b></span>
        )}
      </div>

      <div className={cn(text.muted, 'flex flex-wrap gap-x-7 gap-y-2 mt-4 opacity-80')}>
        {b.plan_total_at_mid_tier != null && <span>{L.mid_tier_total} <b className="text-foreground tabular-nums">{money(b.plan_total_at_mid_tier)}</b></span>}
        {b.plan_total_at_premium_tier != null && <span>{L.premium_tier_total} <b className="text-foreground tabular-nums">{money(b.plan_total_at_premium_tier)}</b></span>}
      </div>
    </PlanLensFrame>
  );
}
